import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaVideo,
  FaDownload,
  FaTimes,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaPlay,
  FaFileVideo,
} from "react-icons/fa";
import { HiOutlineSearch, HiOutlinePhotograph, HiOutlineVideoCamera } from "react-icons/hi";
import { MdRemoveRedEye } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";

const Gallery = () => {
  // Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ALL ORIGINAL STATE
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    image: null,
    video: null,
    imagePreview: "",
    videoPreview: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [mediaFilter, setMediaFilter] = useState("all"); // 'all', 'images', 'videos'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest'

  const itemsPerPage = 10;
  const authToken = Cookies.get("authToken");

  const transparentFallback =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = galleryItems.length;
    const images = galleryItems.filter(item => item.image).length;
    const videos = galleryItems.filter(item => item.video).length;
    return { total, images, videos };
  }, [galleryItems]);

  // Filter and sort items
  useEffect(() => {
    let filtered = [...galleryItems];
    
    // Apply search filter
    if (debouncedSearchTerm.trim() !== "") {
      filtered = filtered.filter(item => 
        item._id?.includes(debouncedSearchTerm)
      );
    }
    
    // Apply media type filter
    if (mediaFilter === 'images') {
      filtered = filtered.filter(item => item.image);
    } else if (mediaFilter === 'videos') {
      filtered = filtered.filter(item => item.video);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredItems(filtered);
    setCurrentPage(1);
  }, [debouncedSearchTerm, galleryItems, mediaFilter, sortBy]);

  useEffect(() => {
    const fetchGalleryItems = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllGallery`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        if (!data.success || !Array.isArray(data.data)) {
          throw new Error("Invalid data format received from server");
        }

        setGalleryItems(data.data);
        setFilteredItems(data.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching gallery items:", err);
        setError("Failed to load gallery items: " + err.message);
        setGalleryItems([]);
        setFilteredItems([]);
        toast.error("Failed to load gallery items");
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryItems();
  }, [authToken]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const currentData = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const canPlayVideo = (url) => {
    const supportedFormats = [".mp4", ".webm", ".ogg", ".mov"];
    return supportedFormats.some((format) =>
      url?.toLowerCase().includes(format)
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    }
  };

  // Preview modal handlers
  const openPreviewModal = (item, type) => {
    setSelectedMedia({ ...item, type });
    setShowPreviewModal(true);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedMedia(null);
  };

  // Add/Edit modal handlers
  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentItem(null);
    setFormData({
      image: null,
      video: null,
      imagePreview: "",
      videoPreview: "",
    });
    setShowAddEditModal(true);
  };

  const openEditModal = (item) => {
    setIsEditMode(true);
    setCurrentItem(item);
    setFormData({
      image: null,
      video: null,
      imagePreview: item.image || "",
      videoPreview: item.video || "",
    });
    setShowAddEditModal(true);
  };

  const closeAddEditModal = () => {
    setShowAddEditModal(false);
    setIsEditMode(false);
    setCurrentItem(null);
    setFormData({
      image: null,
      video: null,
      imagePreview: "",
      videoPreview: "",
    });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        [type]: file,
        [`${type}Preview`]: url,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.image && !formData.video) {
      toast.error("Please select at least one file");
      return;
    }

    const formDataToSend = new FormData();

    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }
    if (formData.video) {
      formDataToSend.append("video", formData.video);
    }

    const url = isEditMode
      ? `${process.env.REACT_APP_BACKEND_URL}/apis/admin/updateGallery/${currentItem._id}`
      : `${process.env.REACT_APP_BACKEND_URL}/apis/admin/createGallery`;

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Operation failed");
      }

      // Refresh the gallery items
      const refreshResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllGallery`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const refreshData = await refreshResponse.json();
      setGalleryItems(refreshData.data);
      setFilteredItems(refreshData.data);

      closeAddEditModal();
      toast.success(
        `Item ${isEditMode ? "updated" : "added"} successfully`
      );
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error(
        `Failed to ${isEditMode ? "update" : "add"} gallery item`
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete modal handlers
  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setIsDeleting(false);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deleteGallery/${itemToDelete._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to delete item");
      }

      // Update state
      setGalleryItems((prev) => {
        const updatedItems = prev.filter((item) => item._id !== itemToDelete._id);
        return updatedItems;
      });

      toast.success("Item deleted successfully");
      closeDeleteModal();
    } catch (err) {
      console.error("Error deleting item:", err);
      toast.error(err.message || "Failed to delete gallery item");
    } finally {
      setIsDeleting(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </motion.div>
  );

  const renderSkeletonCards = () => {
    return Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex space-x-3">
          <Skeleton width={80} height={80} />
          <div className="flex-1">
            <Skeleton width="60%" height={16} />
            <Skeleton width="40%" height={14} className="mt-2" />
            <div className="flex justify-end gap-2 mt-3">
              <Skeleton width={32} height={32} circle />
              <Skeleton width={32} height={32} circle />
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderSkeletonRows = () =>
    Array(5)
      .fill(0)
      .map((_, index) => (
        <tr key={`skeleton-${index}`} className="even:bg-gray-50">
          <td className="px-6 py-4"><Skeleton width={80} height={80} /></td>
          <td className="px-6 py-4"><Skeleton width={150} /></td>
          <td className="px-6 py-4"><Skeleton width={100} /></td>
          <td className="px-6 py-4"><Skeleton width={80} /></td>
        </tr>
      ));

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
        <HiOutlinePhotograph className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {searchTerm || mediaFilter !== 'all' ? 'No items found' : 'No media yet'}
      </h3>
      <p className="text-gray-500 mb-6">
        {searchTerm || mediaFilter !== 'all'
          ? 'Try adjusting your search or filters'
          : 'Add your first image or video to the gallery'}
      </p>
      {(searchTerm || mediaFilter !== 'all') && (
        <button
          onClick={() => {
            setSearchTerm('');
            setMediaFilter('all');
          }}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <FaTimes className="w-4 h-4 mr-2" />
          Clear filters
        </button>
      )}
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/gallery" />
      
      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />
        
        <main className="p-4 lg:p-6 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200"
          >
            {/* Header with stats */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <HiOutlinePhotograph className="w-8 h-8 text-gray-600" />
                    Gallery Management
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found
                    {searchTerm && ` for "${searchTerm}"`}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  {/* <div className="relative flex-1 sm:min-w-[300px]">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700 transition-shadow"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    )}
                  </div> */}
                  
                  <button
                    className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors shadow-sm"
                    onClick={openAddModal}
                  >
                    <FaPlus /> Add Media
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              {!loading && galleryItems.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <StatCard title="Total Items" value={stats.total} icon={HiOutlinePhotograph} color="gray" />
                  <StatCard title="Images" value={stats.images} icon={FaImage} color="blue" />
                  <StatCard title="Videos" value={stats.videos} icon={FaVideo} color="green" />
                </div>
              )}
            </div>

            {/* Filters and Sort */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
              <div className="flex gap-2 overflow-x-auto pb-2">
                <button
                  onClick={() => setMediaFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    mediaFilter === 'all'
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({stats.total})
                </button>
                <button
                  onClick={() => setMediaFilter('images')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    mediaFilter === 'images'
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Images ({stats.images})
                </button>
                <button
                  onClick={() => setMediaFilter('videos')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    mediaFilter === 'videos'
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Videos ({stats.videos})
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
              >
                <p className="text-red-600 text-sm">{error}</p>
              </motion.div>
            )}

            {/* Mobile Card View */}
            <div className="block lg:hidden">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {renderSkeletonCards()}
                  </motion.div>
                ) : currentData.length === 0 ? (
                  renderEmptyState()
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {currentData.map((item) => (
                      <motion.div
                        key={item._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex space-x-3">
                          <div className="relative group">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt="Gallery"
                                className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-gray-200"
                                onClick={() => openPreviewModal(item, "image")}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = transparentFallback;
                                }}
                              />
                            ) : (
                              <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs border border-gray-200">
                                No Image
                              </div>
                            )}
                            {item.image && (
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center cursor-pointer"
                                   onClick={() => openPreviewModal(item, "image")}>
                                <MdRemoveRedEye className="text-white opacity-0 group-hover:opacity-100" size={20} />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  {item.image && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                      Image
                                    </span>
                                  )}
                                  {item.video && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                      Video
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400">{formatDate(item.createdAt)}</p>
                                {item.video && (
                                  <div className="mt-2">
                                    {canPlayVideo(item.video) ? (
                                      <button
                                        className="text-gray-600 hover:text-gray-800 text-sm flex items-center gap-1 transition-colors"
                                        onClick={() => openPreviewModal(item, "video")}
                                      >
                                        <FaPlay size={12} /> Preview Video
                                      </button>
                                    ) : (
                                      <a
                                        href={item.video}
                                        download
                                        className="text-gray-600 hover:text-gray-800 text-sm flex items-center gap-1 transition-colors"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <FaDownload size={12} /> Download
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openEditModal(item)}
                                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <FaEdit size={16} />
                                </button>
                                <button
                                  onClick={() => openDeleteModal(item)}
                                  className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <FaTrash size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Added</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      renderSkeletonRows()
                    ) : currentData.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12">
                          {renderEmptyState()}
                        </td>
                      </tr>
                    ) : (
                      currentData.map((item, index) => (
                        <motion.tr
                          key={item._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="relative group">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt="Gallery"
                                  className="w-16 h-16 object-cover rounded-lg cursor-pointer border border-gray-200"
                                  onClick={() => openPreviewModal(item, "image")}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = transparentFallback;
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs border border-gray-200">
                                  No Image
                                </div>
                              )}
                              {item.image && (
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center cursor-pointer"
                                     onClick={() => openPreviewModal(item, "image")}>
                                  <MdRemoveRedEye className="text-white opacity-0 group-hover:opacity-100" size={20} />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {item.image && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  Image
                                </span>
                              )}
                              {item.video && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  Video
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {item.video ? (
                              canPlayVideo(item.video) ? (
                                <button
                                  className="text-gray-600 hover:text-gray-800 flex items-center gap-2 text-sm transition-colors"
                                  onClick={() => openPreviewModal(item, "video")}
                                >
                                  <FaPlay size={12} /> Preview
                                </button>
                              ) : (
                                <a
                                  href={item.video}
                                  download
                                  className="text-gray-600 hover:text-gray-800 flex items-center gap-2 text-sm transition-colors"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaDownload size={12} /> Download
                                </a>
                              )
                            ) : (
                              <span className="text-gray-400 text-sm">No video</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(item.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openEditModal(item)}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <FaEdit size={18} />
                              </button>
                              <button
                                onClick={() => openDeleteModal(item)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <FaTrash size={18} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredItems.length > itemsPerPage && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredItems.length)} of{' '}
                  {filteredItems.length} items
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Media Preview Modal */}
      <AnimatePresence>
        {showPreviewModal && selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closePreviewModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-4 lg:p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-800">
                  {selectedMedia.type === "image" ? "Image Preview" : "Video Preview"}
                </h3>
                <button
                  onClick={closePreviewModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {selectedMedia.type === "image" ? (
                <img
                  src={selectedMedia.image || transparentFallback}
                  alt="Preview"
                  className="w-full max-h-[70vh] object-contain rounded mb-4 bg-gray-100 border border-gray-200"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = transparentFallback;
                  }}
                />
              ) : canPlayVideo(selectedMedia.video) ? (
                <video controls className="w-full max-h-[70vh] rounded mb-4 bg-gray-100 border border-gray-200">
                  <source src={selectedMedia.video} type="video/mp4" />
                  Your browser does not support this video format.
                </video>
              ) : (
                <div className="text-center p-8">
                  <FaFileVideo className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-base lg:text-lg mb-4 text-gray-700">
                    Video format not supported in browser
                  </p>
                  <a
                    href={selectedMedia.video}
                    download
                    className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded inline-flex items-center gap-2 text-sm transition-colors"
                  >
                    <FaDownload /> Download Video
                  </a>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <button
                  onClick={closePreviewModal}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Media Modal */}
      <AnimatePresence>
        {showAddEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeAddEditModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-4 lg:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-800">
                  {isEditMode ? "Edit Media" : "Add Media"}
                </h3>
                <button
                  onClick={closeAddEditModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Image <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "image")}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-colors"
                    />
                  </div>
                  {(formData.imagePreview || (isEditMode && currentItem?.image)) && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">Preview:</p>
                      <img
                        src={formData.imagePreview || currentItem?.image}
                        alt="Preview"
                        className="w-full h-32 object-contain rounded-lg bg-gray-100 border border-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = transparentFallback;
                        }}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Video <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => handleFileChange(e, "video")}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-colors"
                    />
                  </div>
                  {(formData.videoPreview || (isEditMode && currentItem?.video)) &&
                    (canPlayVideo(formData.videoPreview || currentItem?.video) ? (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-2">Preview:</p>
                        <video controls className="w-full h-32 object-contain rounded-lg bg-gray-100 border border-gray-200">
                          <source
                            src={formData.videoPreview || currentItem?.video}
                            type="video/mp4"
                          />
                        </video>
                      </div>
                    ) : (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                        {isEditMode
                          ? "Existing video file"
                          : "Video selected"}
                      </div>
                    ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6">
                <button
                  onClick={closeAddEditModal}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm transition-colors flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {loading
                    ? isEditMode ? "Updating..." : "Adding..."
                    : isEditMode ? "Update" : "Add"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && itemToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeDeleteModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg lg:text-xl font-bold text-gray-800">Delete Item</h3>
                <button
                  onClick={closeDeleteModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  disabled={isDeleting}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <FaExclamationTriangle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">This action cannot be undone.</p>
                </div>
                
                <p className="text-sm lg:text-base text-gray-700 mb-4">
                  Are you sure you want to delete this gallery item?
                </p>
                
                {itemToDelete.image && (
                  <div className="flex justify-center">
                    <img
                      src={itemToDelete.image}
                      alt="Preview"
                      className="h-24 sm:h-32 object-contain rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = transparentFallback;
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteItem}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center text-sm transition-colors disabled:opacity-50"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Delete Item"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;